import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import "./style.css";
import type { RateTableFormValues } from "../types";

export interface RateTableFormOption {
  value: string;
  label: string;
}

export interface RateTableFormProps {
  id: string;
  initialValues: RateTableFormValues;
  currencyOptions: RateTableFormOption[];
  showCurrencySelect: boolean;
  showDefaultToggle: boolean;
  isSubmitting: boolean;
  onSubmit: (values: RateTableFormValues) => void;
}

export const RateTableForm = ({
  id,
  initialValues,
  currencyOptions,
  showCurrencySelect,
  showDefaultToggle,
  isSubmitting,
  onSubmit,
}: RateTableFormProps): JSX.Element => {
  const [values, setValues] = useState<RateTableFormValues>(initialValues);

  const set = <K extends keyof RateTableFormValues>(
    key: K,
    v: RateTableFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  const selectedCurrency =
    currencyOptions.find((o) => o.value === values.currencyId) ?? undefined;

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-3">
          <Form.Label htmlFor="rate-table-name">Name</Form.Label>
          <TextField
            label={null}
            id="rate-table-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        {showCurrencySelect && (
          <div className="col-sm-3">
            <RequiredReactSelect
              label="Currency"
              value={selectedCurrency}
              onChange={(opt: any) => set("currencyId", opt?.value ?? "")}
              options={currencyOptions}
              placeholder="Select…"
              required
            />
          </div>
        )}
        <div className="col-sm-3">
          <Form.Label htmlFor="rate-table-accounting-code">
            Accounting Code
          </Form.Label>
          <TextField
            label={null}
            id="rate-table-accounting-code"
            value={values.accountingCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("accountingCode", e.target.value)
            }
          />
        </div>
        {showDefaultToggle && (
          <div className="col-sm-3">
            <Form.Label>Default?</Form.Label>
            <div className="rateTableDefaultToggle">
              <GenericToggle
                key={`default-${initialValues.default}`}
                initialValueOfIsOn={values.default}
                onToggle={(isOn: boolean) => set("default", isOn)}
                onText="Default"
                offText="Not default"
                hasText
              />
            </div>
            <small className="small text-muted d-block">
              When checked, new projects will initially use this rate table.
            </small>
          </div>
        )}
      </div>
    </Form>
  );
};

export default RateTableForm;
